import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MAX_LEAGUE_NAME_CHARACTERS } from "../../Common/constants";
import {
  disableEmojiInput,
  getTranslations,
  isMobile,
} from "../../Common/utils";
import { league } from "../../redux/actions";
import { AffordableToggleSwitch } from "../AffordableToggleSwitch";
import { ErrorMessage } from "../Error/message";
import { Divider } from "../Divider";
import { CtaButton } from "../Buttons";
import { getLeagueTypeName } from "../../Common/utils/league";

const CreateEditLeagueForm = ({ isEdit = false, canEditLeague = () => {} }) => {
  const dispatch = useDispatch();
  const gameplayState = useSelector((state) => state.gameplay);
  const leagueState = useSelector((state) => state.league);
  const { selectedLeague, leagueInfoSuccessData } = leagueState;

  const [leagueType, setLeagueType] = useState(() => {
    if (isEdit) {
      return getLeagueTypeName(selectedLeague?.league_type);
    }
    return "";
  });
  const [leagueName, setLeagueName] = useState(() => {
    if (isEdit) {
      return decodeURIComponent(selectedLeague?.league_name);
    }
    return "";
  });
  const [maxParticipants, setMaxParticipants] = useState(() => {
    if (isEdit) {
      return leagueInfoSuccessData?.maxMem;
    }
    return 10;
  });
  const [maxTeams, setMaxTeams] = useState(() => {
    if (isEdit) {
      return leagueInfoSuccessData?.noOfteam;
    }
    return 1;
  });
  const [checked, setChecked] = useState(() => {
    if (isEdit) {
      return leagueInfoSuccessData?.maxMem === 99999999;
    }
    return false;
  });

  useEffect(() => {
    canEditLeague({ leagueName, maxParticipants });
  }, [canEditLeague, leagueName, maxParticipants]);

  useEffect(() => {
    if (isEdit) {
      dispatch(
        league.getLeagueInfo({ leagueCode: selectedLeague?.league_code })
      );
    }
  }, [dispatch, isEdit, selectedLeague?.league_id]);

  // Populate form fields when leagueInfoSuccessData is received from API
  useEffect(() => {
    if (isEdit && leagueInfoSuccessData) {
      setMaxParticipants(leagueInfoSuccessData.maxMem ?? 10);
      setMaxTeams(leagueInfoSuccessData.noOfteam ?? 1);
      setChecked(leagueInfoSuccessData.maxMem === 99999999);
      setLeagueType(getLeagueTypeName(selectedLeague?.league_type));
    }
  }, [isEdit, leagueInfoSuccessData, selectedLeague]);

  const handleLeagueTypeChange = (e) => {
    setLeagueType(e.target.value);
  };
  const handleLeagueNameChange = (e) => {
    let val = disableEmojiInput(e.target.value);
    setLeagueName(val);
    // setLeagueName(e.target.value);
  };
  const handleMaxParticipantsChange = (e) => {
    // if (leagueType == "private" && Number(e.target.value) < 5) {
    //   setMaxParticipants(5);
    // } else {
    setMaxParticipants(Number(e.target.value));
    // }
  };
  const handleMaxTeamsChange = (e) => {
    let value = Number(e.target.value);
    dispatch(league.setMaxTeams(value));
    setMaxTeams(value);
  };

  const handleChecked = () => {
    setChecked(!checked);
  };

  const handlePasteMaxTeam = (event) => {
    event.preventDefault();
    const pastedText = (event.clipboardData || window.clipboardData).getData(
      "text"
    );
    const filteredText = pastedText.replace(/[^0-9]/g, "");
    setMaxParticipants(filteredText);
  };

  const handleIncrement = () => {
    if (maxParticipants < 99999999) {
      setMaxParticipants((prev) => Number(prev) + 1);
    }
  };

  const handleDecrement = () => {
    if (maxParticipants > 2) {
      setMaxParticipants((prev) => Number(prev) - 1);
    }
  };
  useEffect(() => {
    if (checked) {
      setMaxParticipants(99999999);
    } else if (leagueType == "h2h") {
      setMaxParticipants(2);
    } else {
      setMaxParticipants(isEdit ? leagueInfoSuccessData?.maxMem : 10);
    }
  }, [checked, isEdit]);

  useEffect(() => {
    if (leagueType == "h2h") {
      setMaxParticipants(2);
      setChecked(false);
      setMaxTeams(1);
    } else if (leagueType == "public") {
      setChecked(true);
    } else {
      setChecked(false);
      setMaxParticipants(10);
      setMaxTeams(3);
    }
  }, [leagueType]);

  useEffect(() => {
    dispatch(
      league.setCreateLeagueData({
        leagueType,
        leagueName,
        maxParticipants,
        maxTeams,
      })
    );
  }, [leagueType, leagueName, maxParticipants, maxTeams, dispatch]);

  return (
      <div className="si-league__create-edit-form">
        <form className="" action="" method="" aria-label="">
          <div className="si-league__create-edit-form--box">
            <div className="si-input ">
              <label className="si-lbl" htmlFor="createLeagueName">
                <span>
                  {getTranslations(
                    "create_league_popup_league_name_label",
                    "League Name"
                  )}
                </span>
              </label>
              <input
                type="text"
                id="createLeagueName"
                className=""
                placeholder={getTranslations(
                  "create_league_popup_league_name_placeholder",
                  "Enter your league name"
                )}
                value={leagueName}
                spellCheck="false"
                maxLength={MAX_LEAGUE_NAME_CHARACTERS}
                onChange={(e) => handleLeagueNameChange(e)}
              />
              <span className="si-maxLimit">
                {getTranslations(
                  "create_league_popup_league_name_validation",
                  "Max. {{MAX_CHARACTERS}} characters"
                ).replace("{{MAX_CHARACTERS}}", MAX_LEAGUE_NAME_CHARACTERS)}
              </span>
            </div>
          </div>
          <Divider />
          <div
            className={`si-league__create-edit-form--box ${isEdit ? "si-disabled" : ""}`}
          >
            <div className="si-league__overlay-box-radio">
              <h2 className="si-league__overlay-box-title">
                <span>
                  {getTranslations(
                    "create_league_popup_choose_type_caption",
                    "Choose a type of league"
                  )}{" "}
                  {isEdit && <i className="f1i-lock" />}
                </span>
              </h2>
              <div className="si-radioBtn__wrap">
                <div
                  className={`si-radioBtn ${
                    leagueType === "private" ? "si-classic" : ""
                  }`}
                >
                  <input
                    type="radio"
                    id="createPrivateLeague"
                    name="leagueType"
                    value="private"
                    checked={leagueType === "private"}
                    onChange={(e) => handleLeagueTypeChange(e)}
                  />
                  <label htmlFor="createPrivateLeague">
                    <div className="si-league__radio-title">
                      <span>
                        {getTranslations("create_league_classic  ", "Classic")}
                      </span>
                    </div>
                    <div className="si-league__radio-subTitle">
                      {getTranslations(
                        "create_league_popup_private_league_badge",
                        "Private"
                      )}
                    </div>
                    <div className="si-league__radio-caption">
                      <span>
                        {getTranslations(
                          "create_league_popup_private_league_caption",
                          "Can join only through invite"
                        )}
                      </span>
                    </div>
                    {/* <PvtPubCardChips PvtPubCardChipsNameConfig={([0], [0])} /> */}
                  </label>
                </div>
                <Divider direction={isMobile() ? "horizontal" : "vertical"} />
                <div
                  className={`si-radioBtn ${
                    leagueType === "h2h" ? "si-h2h" : ""
                  }`}
                >
                  <input
                    type="radio"
                    id="createH2hLeague"
                    name="leagueType"
                    value="h2h"
                    checked={leagueType === "h2h"}
                    onChange={(e) => handleLeagueTypeChange(e)}
                  />
                  <label htmlFor="createH2hLeague">
                    <div className="si-league__radio-title">
                      <span>
                        {getTranslations("create_league_h2h", "Head-2-Head")}
                      </span>
                    </div>
                    <div className="si-league__radio-subTitle">
                      {getTranslations(
                        "create_league_popup_h2h_league_badge",
                        "H2H"
                      )}
                    </div>
                    <div className="si-league__radio-caption">
                      <span>
                        {getTranslations(
                          "create_league_popup_h2h_league_caption",
                          "Compete against just one other player in this league"
                        )}
                      </span>
                    </div>
                    {/* <PvtPubCardChips PvtPubCardChipsNameConfig={([1], [1])} /> */}
                  </label>
                </div>
                <Divider direction={isMobile() ? "horizontal" : "vertical"} />
                <div
                  className={`si-radioBtn ${
                    leagueType === "public" ? "si-public" : ""
                  }`}
                >
                  <input
                    type="radio"
                    id="createPublicLeague"
                    name="leagueType"
                    value="public"
                    checked={leagueType === "public"}
                    onChange={(e) => handleLeagueTypeChange(e)}
                  />
                  <label htmlFor="createPublicLeague">
                    <div className="si-league__radio-title">
                      <span>
                        {getTranslations("create_league_public", "Public")}
                      </span>
                    </div>
                    {/* <div className="si-league__radio-subTitle">
                      {getTranslations(
                        "create_league_popup_public_league_badge",
                        "Public"
                      )}
                    </div> */}
                    <div className="si-league__radio-caption">
                      <span>
                        {getTranslations(
                          "create_league_popup_public_league_caption",
                          "Anyone can join -- no limits"
                        )}
                      </span>
                    </div>
                    {/* <PvtPubCardChips PvtPubCardChipsNameConfig={([2], [2])} /> */}
                  </label>
                </div>
              </div>
            </div>
          </div>
          <Divider />
          <div
            className={`si-league__create-edit-form--box ${isEdit && leagueInfoSuccessData?.maxMem === 99999999 ? "si-disabled" : ""}`}
          >
            <AffordableToggleSwitch
              extraClass={isEdit ? "si-radioBtn__lbl--locked" : ""}
              toggleLabelText={getTranslations(
                "create_league_popup_unlimited_participants_toggle",
                "Unlimited no. of participants"
              )}
              toggleLabelIcon={isEdit}
              handleChecked={handleChecked}
              checked={checked}
              leagueType={leagueType}
            />
          </div>
          <Divider />
          <div className="si-league__create-edit-form--box">
            <div className=" si-league__overlay-box--grid si-league__create-edit-form--teamInputs">
              <div className="si-input ">
                <label className="si-lbl" htmlFor="maximumParticipants">
                  <span>
                    {getTranslations(
                      "create_league_popup_maximum_participants_label",
                      "Maximum Participants"
                    )}
                  </span>
                </label>
                <div className="si-numberStepper">
                  <CtaButton
                    variant="ghost"
                    size="md"
                    disabled={
                      checked ||
                      leagueType == "h2h" ||
                      maxParticipants <= 2 ||
                      (isEdit &&
                        maxParticipants <= leagueInfoSuccessData?.maxMem)
                    }
                    onClick={handleDecrement}
                    classes="si-numberStepper__btn"
                    restProps={{ type: "button" }}
                    icon={"f1i-subtract"}
                    onlyIcon={true}
                  ></CtaButton>
                  <input
                    type={checked ? "text" : "number"}
                    id="maximumParticipants"
                    className="si-numberStepper__input"
                    placeholder="10"
                    disabled={checked || leagueType == "h2h"}
                    value={checked ? "-" : maxParticipants}
                    min={isEdit ? maxParticipants : 2}
                    max={99999999}
                    spellCheck="false"
                    onChange={(e) => handleMaxParticipantsChange(e)}
                    onKeyDown={(event) => {
                      if (event.key === ".") event.preventDefault();
                    }}
                    onPaste={handlePasteMaxTeam}
                  />
                  <CtaButton
                    variant="ghost"
                    size="md"
                    disabled={
                      checked ||
                      leagueType == "h2h" ||
                      maxParticipants >= 99999999
                    }
                    onClick={handleIncrement}
                    classes="si-numberStepper__btn"
                    restProps={{ type: "button" }}
                    icon="f1i-add"
                    onlyIcon={true}
                  ></CtaButton>
                </div>
              </div>
              <div className={`si-input ${isEdit ? "si-disabled" : ""}`}>
                <label className="si-lbl" htmlFor="numberTeamsAllow">
                  <span>
                    {getTranslations(
                      "create_league_popup_maximum_teams_label_short",
                      "Teams per user"
                    )}{" "}
                    {isEdit && <i className="f1i-lock" />}
                  </span>
                </label>
                <select
                  id="numberTeamsAllow"
                  name="numberTeamsAllow"
                  aria-disabled="true"
                  aria-expanded="true"
                  aria-label="Select your country"
                  disabled={leagueType == "h2h"}
                  value={maxTeams}
                  onChange={(e) => handleMaxTeamsChange(e)}
                >
                  <option value={1}>1</option>
                  <option value={2}>2</option>
                  <option value={3}>3</option>
                </select>
              </div>
            </div>
          </div>
          {isEdit && (
            <ErrorMessage
              text={getTranslations(
                "edit_league_max_participants_message",
                "*Maximum Participants can be increased only by a count of 10 or more"
              )}
            />
          )}
        </form>
      </div>
  );
};

export { CreateEditLeagueForm };
